/**
 * One tone with an envelope on it: how everything here is sounded.
 *
 * Two voices, asked for in that order. A caller whose engine has recordings of real
 * instruments gets one of those, chosen by the note's General MIDI program; everyone else,
 * and every note the recordings cannot cover, gets a plain oscillator with a short ramp up
 * and down. The oscillator is not a stopgap -- this is a notation editor and the point of
 * hearing a score is to find out whether it is right, which an oscillator answers -- but a
 * page that has been given an orchestra should not be made to play one.
 *
 * The envelope is written as ramps rather than as one value so that a note beginning while
 * another on the same pitch is still releasing does not click, and so that a chord does not
 * add up to a clipped signal.
 */

import type {
  AudioEngine,
  AudioValue,
  GainLike,
  PlayedOn,
  SoundingTone,
  SwellPoint,
  ToneLike,
} from "./port";

/** The shape of the voice: what it is made of and how it starts and stops. */
export interface ToneShape {
  readonly wave: string;
  /** Seconds of ramp up and down, which is what keeps a note from clicking. */
  readonly attack: number;
  readonly release: number;
}

/** Equal temperament, A4 = 440 Hz. */
export function hertz(midi: number): number {
  return 440 * 2 ** ((midi - 69) / 12);
}

/**
 * Move a held note's loudness through its swell, between the end of its attack and the start
 * of its release, and hold the last of it until the release.
 *
 * Exported for an engine sounding recordings, which has the same envelope to write.
 */
export function swellInto(
  gain: AudioValue,
  swell: readonly SwellPoint[],
  held: number,
  released: number,
  scale = 1,
): void {
  let last: number | null = null;
  for (const point of swell) {
    const when = Math.min(Math.max(point.at, held), released);
    gain.linearRampToValueAtTime(point.peak * scale, when);
    last = point.peak * scale;
  }
  if (last !== null) gain.setValueAtTime(last, released);
}

/**
 * Schedule one tone. It stops itself at the end of its envelope.
 *
 * ``played`` names the instrument and the key, and is what lets an engine with recordings
 * sound the note on one. It is optional because not every sound in a score is a note: the
 * metronome has no instrument, and neither has a key echoed back from a keyboard before
 * anybody has said what plays it.
 *
 * The sound comes back so that a caller which may need to cut it short -- a player being
 * stopped, or a playhead being dragged -- can stop it. A caller with nothing to cut short
 * can drop it on the floor.
 *
 * ``swell`` is where the loudness moves while the note is held; see `VoiceRequest.swell`.
 */
export function sound(
  engine: AudioEngine,
  shape: ToneShape,
  hz: number,
  at: number,
  length: number,
  peak: number,
  played: PlayedOn | null = null,
  swell: readonly SwellPoint[] = [],
): SoundingTone {
  if (played !== null) {
    const recorded = engine.createVoice?.({ ...played, at, length, peak, swell });
    if (recorded != null) return recorded;
  }

  const source = engine.createOscillator();
  const gain = engine.createGain();
  source.type = shape.wave;
  source.frequency.setValueAtTime(hz, at);

  const end = at + Math.max(length, shape.attack + shape.release);
  gain.gain.setValueAtTime(0, at);
  gain.gain.linearRampToValueAtTime(peak, at + shape.attack);
  const released = Math.max(at + shape.attack, end - shape.release);
  swellInto(gain.gain, swell, at + shape.attack, released);
  if (swell.length === 0) gain.gain.setValueAtTime(peak, released);
  gain.gain.linearRampToValueAtTime(0, end);

  source.connect(gain);
  gain.connect(engine.destination);
  source.start(at);
  source.stop(end);
  return { until: end, stop: (when) => release(source, gain, when) };
}

/**
 * Cut a scheduled tone short and let its nodes go.
 *
 * The disconnection happens whether or not the stop was accepted, because a node that
 * cannot be stopped is exactly the one that must not be left connected.
 */
function release(source: ToneLike, gain: GainLike, when: number): void {
  try {
    source.stop(when);
  } finally {
    source.disconnect();
    gain.disconnect();
  }
}
